// Pure, view-agnostic helpers for the admin SPIN pages — extracted from AdminSpinPage so the dashboard
// and its sub-pages share one copy and the logic is unit-testable (mirrors ProjectPage/OtuDataTableView.logic.ts).
import type { FanoutBucket } from '../../../Generated/FanoutBucket.ts'
import type { RefSplit } from '../../../Generated/RefSplit.ts'
import type { SpinSizeCount } from '../../../Generated/SpinSizeCount.ts'
import type { SpinUnityRow } from '../../../Generated/SpinUnityRow.ts'
import type { RefSplitOrder, SpinReferenceKind } from '../../../Repository/Admin/AdminSpinRepository.js'
import type { Datum } from '../AdminStatsPage/statsAggregations.js'
import { NEAR_PERFECT_IDENTITY } from './spinRefDetail.logic.js'

/** Taxonomy ladder rank labels, index = rank (0=domain … 7=species). */
export const RANK_LABELS = ['domain', 'kingdom', 'phylum', 'class', 'order', 'family', 'genus', 'species']

/** Known taxonomy sources in ladder order; any extras get appended. */
export const KNOWN_SOURCES = ['BOLD', 'NCBI', 'RDP', 'Consensus']

/** Number of equal bins over 0..1 for the unity / reach distributions. */
export const UNITY_BINS = 10

export const FANOUT_ORDER = ['1', '2', '3-5', '6+']

/** Fan-out buckets → ordered chart data. */
export const fanoutData = (buckets: FanoutBucket[]): Datum[] =>
  [...buckets]
    .sort((a, b) => FANOUT_ORDER.indexOf(a.bucket) - FANOUT_ORDER.indexOf(b.bucket))
    .map((x) => ({ name: x.bucket, value: x.count }))

/** Per-rank drift counts → chart data over the full ladder (0-filled where no drift). */
export const driftRankData = (perRank: Array<{ rank: number; changed: number }>): Datum[] => {
  const byRank = new Map(perRank.map((r) => [r.rank, r.changed]))
  return RANK_LABELS.map((label, rank) => ({ name: label, value: byRank.get(rank) ?? 0 }))
}

/** Short SPIN id for compact tables. */
export const shortId = (id: string): string => id.slice(0, 8)

/** null/empty → "∅" so a lost-resolution drift reads clearly in the before→after column. */
export const driftVal = (v: string | null): string => (v == null || v === '' ? '∅' : v)

/** Known sources first (ladder order), unknown extras after alphabetically. Shared source ordering. */
export const orderSources = (seen: Iterable<string>): string[] => {
  const set = new Set(seen)
  const known = KNOWN_SOURCES.filter((k) => set.has(k))
  const extra = [...set].filter((k) => !KNOWN_SOURCES.includes(k)).sort()
  return [...known, ...extra]
}

/** Distinct taxonomy sources present across unity rows, known-first then extras sorted. */
export const sourcesOf = (spins: SpinUnityRow[]): string[] => {
  const seen = new Set<string>()
  spins.forEach((s) => Object.keys(s.unityBySource ?? {}).forEach((k) => seen.add(k)))
  return orderSources(seen)
}

/**
 * Bucketed member-count distribution over the WHOLE current set. Driven by `metrics.sizeDistribution`
 * (one entry per distinct size, summed here into buckets) — NOT the `spins` detail list, which is
 * capped at the request limit and would drop the small-cluster tail (the bug that made this chart
 * disagree with "avg members / SPIN").
 */
export const sizeHistogram = (dist: SpinSizeCount[]): Datum[] => {
  const defs: Array<{ name: string; test: (n: number) => boolean }> = [
    { name: '1', test: (n) => n === 1 },
    { name: '2', test: (n) => n === 2 },
    { name: '3–5', test: (n) => n >= 3 && n <= 5 },
    { name: '6–10', test: (n) => n >= 6 && n <= 10 },
    { name: '11–25', test: (n) => n >= 11 && n <= 25 },
    { name: '26–50', test: (n) => n >= 26 && n <= 50 },
    { name: '51–100', test: (n) => n >= 51 && n <= 100 },
    { name: '100+', test: (n) => n > 100 }
  ]
  return defs.map((d) => ({
    name: d.name,
    value: dist.reduce((sum, s) => (d.test(s.memberCount) ? sum + s.spinCount : sum), 0)
  }))
}

/** 10-bin (0..1) histogram of a per-SPIN score picked from each row; used by unity & reach. */
const scoreHistogram = (spins: SpinUnityRow[], pick: (s: SpinUnityRow) => number | undefined): Datum[] => {
  const bins: Datum[] = Array.from({ length: UNITY_BINS }, (_, i) => ({
    name: `${(i / UNITY_BINS).toFixed(1)}–${((i + 1) / UNITY_BINS).toFixed(1)}`,
    value: 0
  }))
  spins.forEach((s) => {
    const v = pick(s)
    if (v === undefined) return
    const idx = Math.min(UNITY_BINS - 1, Math.max(0, Math.floor(v * UNITY_BINS)))
    bins[idx]!.value += 1
  })
  return bins
}

/** Unity-score distribution (10 equal bins over 0..1) for one source. */
export const unityHistogram = (spins: SpinUnityRow[], source: string): Datum[] =>
  scoreHistogram(spins, (s) => (s.unityBySource ?? {})[source])

/**
 * Reach-score distribution (10 equal bins over 0..1) for one source. Reach is already normalized to
 * 0..1 on the row (backend divides filled ranks by the per-source denominator), so it shares unity's
 * binning + clamp.
 */
export const reachHistogram = (spins: SpinUnityRow[], source: string): Datum[] =>
  scoreHistogram(spins, (s) => (s.reachBySource ?? {})[source])

/**
 * Turn a pre-binned count array (e.g. the backend's full-set unity/reach histogram) into chart data,
 * labelling equal 0..1 bins the same way [unityHistogram] does so the two are directly comparable.
 */
export const histogramToData = (counts: number[]): Datum[] =>
  counts.map((c, i) => ({ name: `${(i / counts.length).toFixed(1)}–${((i + 1) / counts.length).toFixed(1)}`, value: c }))

export const avgOf = (spins: SpinUnityRow[], pick: (s: SpinUnityRow) => number | undefined): number | null => {
  const vals = spins.map(pick).filter((v): v is number => v !== undefined)
  if (vals.length === 0) return null
  return vals.reduce((a, b) => a + b, 0) / vals.length
}

/** Per-source average unity + reach across the current set — "all taxa at a glance". */
export const perSourceAverages = (spins: SpinUnityRow[], sources: string[]) =>
  sources.map((src) => ({
    source: src,
    avgUnity: avgOf(spins, (s) => (s.unityBySource ?? {})[src]),
    avgReach: avgOf(spins, (s) => (s.reachBySource ?? {})[src])
  }))

/** Lowest unity across the SPIN's sources — used to surface the most-mixed clusters first. */
export const worstUnity = (s: SpinUnityRow): number => {
  const vals = Object.values(s.unityBySource ?? {})
  return vals.length ? Math.min(...vals) : 1
}

export const fmt = (v: number | undefined): string => (v === undefined ? '—' : v.toFixed(3))

/** Compact timestamp label: drop the 'T' and fractional seconds from the ISO timestamp. */
export const tsLabel = (iso: string): string => iso.replace('T', ' ').replace(/\.\d+$/, '')

/** `Genus species` for a taxon column; falls back to whichever half resolved, em dash for neither. */
export const taxonLabel = (t: { genus?: string; species?: string }): string => {
  const parts = [t.genus, t.species].filter((p): p is string => !!p && p.trim() !== '')
  return parts.length === 0 ? '—' : parts.join(' ')
}

/**
 * SPIN detail path, preserving the caller's query string so the run/taxonomy selection survives the hop.
 * Shared: every list of SPINs on this panel links out through it, and they must agree on the route.
 */
export const spinDetailPath = (spinId: string, search = ''): string => {
  const qs = search.startsWith('?') ? search : search ? `?${search}` : ''
  return `/admin/spin/s/${spinId}${qs}`
}

/** Growth chart data (SPIN count per run) from lineage rows. */
export const growthData = (lineage: Array<{ startedAt: string; spins: number }>): Datum[] =>
  lineage.map((g) => ({ name: tsLabel(g.startedAt), value: g.spins }))

// ─── Reference concordance triage (SpinReferencePage) ────────────────────────────────────────────────

/**
 * The three questions the `mostSplit` list can be asked, in the order they should be offered. The
 * ordering is NOT a cosmetic sort toggle: it decides which cohort lands at the top, and the raw
 * fan-out ordering is the least diagnostic of the three (see [refOrderQuestion]). `SUSPICION` is the
 * backend default and stays the default here.
 */
export const REF_ORDER_OPTIONS: Array<{ value: RefSplitOrder; label: string }> = [
  { value: 'SUSPICION', label: 'Split despite a near-perfect match (suspicion)' },
  { value: 'OTUS', label: 'Most affected OTUs (blast radius)' },
  { value: 'SPINS', label: 'Largest raw fan-out (most SPINs)' }
]

/**
 * What each ordering actually asks — rendered next to the control so the choice reads as "which
 * question am I asking" rather than a sort direction. The numbers are measured on the real dev DB and
 * are the whole reason `SUSPICION` is the default: ranking by raw fan-out surfaces promiscuous
 * references, where splitting is CORRECT.
 */
export const refOrderQuestion = (order: RefSplitOrder): string => {
  switch (order) {
    case 'SUSPICION':
      return 'Highest minimum identity first: “where did SPIN split ids whose members ALL matched the reference almost perfectly?” This is the diagnostic cohort — 933 reference ids are split across >1 SPIN with a minimum identity ≥ 99.5%.'
    case 'OTUS':
      return 'Most carrying member OTUs first: “how much does the splitting cost us?” Sizes the blast radius, but says nothing about whether the split is wrong.'
    case 'SPINS':
      return 'Largest raw fan-out first: the LEAST diagnostic view. 284 accessions match thousands of unrelated sequences at ~92% identity, and SPIN is right to split those — promiscuity is a property of the reference, not a clustering bug.'
  }
}

/** Identity floors offered as the "minimum identity" filter; '' = send no floor at all. */
export const MIN_IDENTITY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'No identity floor' },
  { value: '90', label: '≥ 90%' },
  { value: '95', label: '≥ 95%' },
  { value: '97', label: '≥ 97%' },
  { value: '99', label: '≥ 99%' },
  { value: String(NEAR_PERFECT_IDENTITY), label: `≥ ${NEAR_PERFECT_IDENTITY}% (near-perfect)` },
  { value: '99.9', label: '≥ 99.9%' }
]

/**
 * Parse the identity-floor control into the optional `minHitPercentage` request param. Empty, junk or
 * out-of-range (percentages are 0..100) ⇒ `undefined` ⇒ the param is omitted and the backend applies no
 * floor. 0 is a legal floor and NOT the same as omitting it: a floor drops ids with no readable
 * identity, because SQL `NULL >= 0` is NULL.
 */
export const parseMinIdentity = (raw: string): number | undefined => {
  const trimmed = raw.trim()
  if (trimmed === '') return undefined
  const v = Number(trimmed)
  if (!Number.isFinite(v) || v < 0 || v > 100) return undefined
  return v
}

/**
 * Why one reference id is split, decided from the triage columns alone (no extra request):
 *  * `AMPLICON` — >1 amplicon, so the split is fully explained: a SPIN is per-amplicon, the same id on
 *    CO1 and 16S MUST land in different SPINs. Checked FIRST; it outranks any identity signal.
 *  * `UNKNOWN_IDENTITY` — no readable minimum identity (e.g. a BOLD bin with no BOLD blast row), so the
 *    split cannot be judged. These are exactly the rows an identity floor silently drops.
 *  * `SUSPICIOUS` — one amplicon and every carrying OTU matched at ≥ [NEAR_PERFECT_IDENTITY]%: SPIN split
 *    despite a near-perfect match. The cohort worth looking at.
 *  * `PROMISCUOUS` — one amplicon but a weak worst match: the reference matches unrelated sequences, so
 *    splitting is the correct answer.
 */
export type RefSplitVerdict = 'AMPLICON' | 'UNKNOWN_IDENTITY' | 'SUSPICIOUS' | 'PROMISCUOUS'

export const splitVerdict = (
  r: Pick<RefSplit, 'minHitPercentage' | 'ampliconCount'>,
  threshold = NEAR_PERFECT_IDENTITY
): RefSplitVerdict => {
  if (r.ampliconCount > 1) return 'AMPLICON'
  if (r.minHitPercentage == null) return 'UNKNOWN_IDENTITY'
  return r.minHitPercentage >= threshold ? 'SUSPICIOUS' : 'PROMISCUOUS'
}

/** Short badge text + explanation per verdict, so table and caption stay in one vocabulary. */
export const VERDICT_UI: Record<RefSplitVerdict, { label: string; tip: string; badge: string }> = {
  AMPLICON: {
    label: 'per-amplicon',
    tip: 'Spans more than one amplicon — a SPIN is per-amplicon, so this split is fully explained and not a clustering fault.',
    badge: 'badge-ghost'
  },
  UNKNOWN_IDENTITY: {
    label: 'no identity',
    tip: 'No readable minimum identity (e.g. a BOLD bin with no BOLD blast row) — the split cannot be judged. An identity floor DROPS these rows.',
    badge: 'badge-outline'
  },
  SUSPICIOUS: {
    label: 'near-perfect',
    tip: `Single amplicon and every carrying OTU matched at ≥ ${NEAR_PERFECT_IDENTITY}% — SPIN split despite a near-perfect match. This is the cohort to investigate.`,
    badge: 'badge-warning'
  },
  PROMISCUOUS: {
    label: 'weak match',
    tip: 'Single amplicon but a weak worst match — the reference matches unrelated sequences, so splitting is correct. Ranking by raw fan-out fills the list with these.',
    badge: 'badge-ghost'
  }
}

/** Verdict counts over the rows actually listed — the one-line "what am I looking at" caption. */
export const splitVerdictCounts = (rows: RefSplit[], threshold = NEAR_PERFECT_IDENTITY): Record<RefSplitVerdict, number> => {
  const counts: Record<RefSplitVerdict, number> = { AMPLICON: 0, UNKNOWN_IDENTITY: 0, SUSPICIOUS: 0, PROMISCUOUS: 0 }
  rows.forEach((r) => {
    counts[splitVerdict(r, threshold)] += 1
  })
  return counts
}

const BOLD_CLUSTER_URI = 'clusteruri='

/**
 * Canonicalise a hand-typed reference id the way the backend's `normaliseRefId` does, so the URL the
 * "jump to id" box builds matches the ids the lists link to: accessions lowercased, a pasted BOLD
 * cluster URL reduced to the `BOLD:…` key, taxon ids only trimmed. Kept in lockstep with
 * SpinReferenceService.normaliseRefId / BOLD_BIN_SQL.
 */
export const normalizeRefIdInput = (kind: SpinReferenceKind, raw: string): string => {
  const trimmed = raw.trim()
  if (kind === 'NCBI_ID') return trimmed.toLowerCase()
  if (kind === 'BOLD_BIN') {
    const i = trimmed.lastIndexOf(BOLD_CLUSTER_URI)
    return i < 0 ? trimmed : trimmed.slice(i + BOLD_CLUSTER_URI.length)
  }
  return trimmed
}

/**
 * Disclosure for a list the backend capped: "showing N of M". Returns null when nothing was cut, so a
 * complete list stays uncluttered. Used for the SPIN detail member window, whose `memberLimit` is
 * independent of the reference-id roll-up.
 */
export const windowNote = (shown: number, total: number, noun: string): string | null =>
  shown >= total ? null : `Showing ${shown.toLocaleString()} of ${total.toLocaleString()} ${noun} — the list is capped.`

export type SortDir = 'asc' | 'desc'

/**
 * Stable numeric sort of [rows] by [pick], honouring [dir]. Pure — returns a new array; used to make
 * admin tables (e.g. the membership-flow list) client-side sortable without mutating the source data.
 */
export const sortByNumber = <T>(rows: T[], pick: (row: T) => number, dir: SortDir): T[] =>
  [...rows].sort((a, b) => (dir === 'asc' ? pick(a) - pick(b) : pick(b) - pick(a)))
